import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  UserIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  LightBulbIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import type { PerfilRelatorioConfig } from '../../services/contentSnapshotService';

interface ProfileContentCardProps {
  profile: PerfilRelatorioConfig & { id: string };
  className?: string;
}

type PlanoTab = '30_dias' | '90_dias' | '6_meses';

const planoTabLabels: Record<PlanoTab, string> = {
  '30_dias': '30 Dias',
  '90_dias': '90 Dias', 
  '6_meses': '6 Meses',
};

const complexityColors: Record<string, string> = {
  'baixa': 'bg-green-100 text-green-800 border-green-200',
  'media': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'alta': 'bg-red-100 text-red-800 border-red-200',
};

export const ProfileContentCard: React.FC<ProfileContentCardProps> = ({ 
  profile, 
  className = '' 
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    description: false,
    strengths: false,
    development: false,
    strategies: false,
  });
  const [activePlanoTab, setActivePlanoTab] = useState<PlanoTab>('30_dias');

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const sectionVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: { 
      height: "auto", 
      opacity: 1,
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <UserIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{profile.nome}</h3>
        </div>
        
        <p className="text-gray-700 leading-relaxed">
          {profile.descricao_resumida}
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Descrição Completa */}
        <div className="border-b border-gray-100 pb-6">
          <button
            onClick={() => toggleSection('description')}
            className="flex items-center justify-between w-full text-left group"
          >
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <LightBulbIcon className="w-5 h-5 text-amber-500" />
              Descrição Detalhada
            </h4>
            {expandedSections.description ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>
          
          <AnimatePresence>
            {expandedSections.description && (
              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="overflow-hidden"
              >
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700 leading-relaxed">
                    {profile.descricao_completa}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Pontos Fortes */}
        <div className="border-b border-gray-100 pb-6">
          <button
            onClick={() => toggleSection('strengths')}
            className="flex items-center justify-between w-full text-left group mb-4"
          >
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <StarIcon className="w-5 h-5 text-yellow-500" />
              Pontos Fortes ({profile.pontos_fortes.length})
            </h4>
            {expandedSections.strengths ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          {/* Preview dos primeiros pontos fortes */}
          <div className="space-y-2">
            {profile.pontos_fortes.slice(0, 2).map((ponto, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="font-semibold text-green-900">{ponto.titulo}</h5>
                  {!expandedSections.strengths && (
                    <p className="text-sm text-green-700 line-clamp-2">{ponto.descricao}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <AnimatePresence>
            {expandedSections.strengths && (
              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="overflow-hidden"
              >
                <div className="mt-4 space-y-4">
                  {profile.pontos_fortes.map((ponto, index) => (
                    <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-100">
                      <h5 className="font-semibold text-green-900 mb-2">{ponto.titulo}</h5>
                      <p className="text-green-800 mb-3">{ponto.descricao}</p>
                      {ponto.exemplo_pratico && (
                        <div className="p-3 bg-green-100 rounded border-l-4 border-green-400">
                          <p className="text-sm text-green-800">
                            <span className="font-medium">Exemplo prático:</span> {ponto.exemplo_pratico}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Áreas de Desenvolvimento */}
        <div className="border-b border-gray-100 pb-6">
          <button
            onClick={() => toggleSection('development')}
            className="flex items-center justify-between w-full text-left group mb-4"
          >
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ArrowTrendingUpIcon className="w-5 h-5 text-blue-500" />
              Áreas de Desenvolvimento ({profile.areas_desenvolvimento.length})
            </h4>
            {expandedSections.development ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          {/* Preview das primeiras áreas */}
          <div className="space-y-2">
            {profile.areas_desenvolvimento.slice(0, 1).map((area, index) => (
              <div key={index} className="p-3 bg-blue-50 rounded-lg">
                <h5 className="font-semibold text-blue-900">{area.titulo}</h5>
                {!expandedSections.development && (
                  <p className="text-sm text-blue-700 line-clamp-2">{area.descricao}</p>
                )}
              </div>
            ))}
          </div>

          <AnimatePresence>
            {expandedSections.development && (
              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="overflow-hidden"
              >
                <div className="mt-4 space-y-4">
                  {profile.areas_desenvolvimento.map((area, index) => (
                    <div key={index} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h5 className="font-semibold text-blue-900 mb-2">{area.titulo}</h5>
                      <p className="text-blue-800 mb-3">{area.descricao}</p>
                      
                      {area.impacto && (
                        <div className="p-3 bg-blue-100 rounded mb-3">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Impacto:</span> {area.impacto}
                          </p>
                        </div>
                      )}

                      {area.sugestoes && area.sugestoes.length > 0 && (
                        <div>
                          <h6 className="font-medium text-blue-900 mb-2">Sugestões:</h6>
                          <ul className="space-y-1">
                            {area.sugestoes.map((sugestao, sugIndex) => (
                              <li key={sugIndex} className="text-sm text-blue-700 flex items-start gap-2">
                                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                                {sugestao}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Plano de Ação */}
        <div className="border-b border-gray-100 pb-6">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <CalendarDaysIcon className="w-5 h-5 text-purple-500" />
            Plano de Ação
          </h4>

          {/* Tabs do Plano */}
          <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
            {(Object.keys(planoTabLabels) as PlanoTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActivePlanoTab(tab)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activePlanoTab === tab
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-purple-600'
                }`}
              >
                {planoTabLabels[tab]}
              </button>
            ))}
          </div>

          {/* Conteúdo da Tab Ativa */}
          <div className="space-y-3">
            {profile.plano_acao[activePlanoTab]?.map((tarefa, index) => (
              <div key={index} className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h5 className="font-semibold text-purple-900 flex-1">{tarefa.tarefa}</h5>
                  {tarefa.complexidade && (
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${
                      complexityColors[tarefa.complexidade.toLowerCase()] || 
                      'bg-gray-100 text-gray-800 border-gray-200'
                    }`}>
                      {tarefa.complexidade}
                    </span>
                  )}
                </div>
                
                <p className="text-purple-800 text-sm mb-2">{tarefa.descricao}</p>
                
                {tarefa.area_melhoria && (
                  <div className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded inline-block">
                    Área: {tarefa.area_melhoria}
                  </div>
                )}
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">
                Nenhuma tarefa definida para este período.
              </p>
            )}
          </div>
        </div>

        {/* Estratégias Específicas */}
        <div>
          <button
            onClick={() => toggleSection('strategies')}
            className="flex items-center justify-between w-full text-left group mb-4"
          >
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <LightBulbIcon className="w-5 h-5 text-orange-500" />
              Estratégias Específicas
            </h4>
            {expandedSections.strategies ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          {/* Preview das categorias */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(profile.estrategias_especificas).map(([categoria, estrategias]) => (
              estrategias.length > 0 && (
                <span key={categoria} className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                  {categoria} ({estrategias.length})
                </span>
              )
            ))}
          </div>

          <AnimatePresence>
            {expandedSections.strategies && (
              <motion.div
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="overflow-hidden"
              >
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(profile.estrategias_especificas).map(([categoria, estrategias]) => (
                    estrategias.length > 0 && (
                      <div key={categoria} className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                        <h5 className="font-semibold text-orange-900 mb-3 capitalize">
                          {categoria}
                        </h5>
                        <ul className="space-y-2">
                          {estrategias.map((estrategia, index) => (
                            <li key={index} className="text-sm text-orange-800 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2 flex-shrink-0"></span>
                              {estrategia}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};